.me-dropdown {
  position: static;
  display: block;
  width: 100%;

  @include desktop-lg {
    display: inline-flex;
    align-items: center;
    width: auto;

    // The <me-button> host has no `display` of its own, so as a flex item of `.me-dropdown` it
    // blockifies to `block` and wraps its `inline-flex; vertical-align: middle` <a> in a LINE
    // BOX. That line box's strut inherits `line-height: normal` from body (global-core sets the
    // font but never a line-height), and the strut's descent hangs below the 24px trigger, so
    // the host measures 24.36px and the label sits 0.36px low. Measured against
    // `sn-navbar-placeholder-type-2`, whose labels are direct flex children of the bar and have
    // no line box, that is a visible ~1px drop the moment the real navbar swaps in
    // (`perf/navbar-parity.html` reports it as `topInBar` 24.36 vs 24). Centering the host's
    // content collapses the strut — same defect and same fix as the `me-logo` host in
    // source/components/product/signnow/navbar/type-1/index.scss.
    // Desktop only: below the collapse breakpoint the burger menu owns this layout
    // (`[slot='area-navbar-mobile'] me-button { display: block }` in navbars/type-1).
    > me-button {
      display: flex;
      align-items: center;
    }
  }

  .me-button {
    justify-content: space-between;
    padding: 0;

    @include desktop-lg {
      width: auto;
      padding: initial;
      justify-content: initial;
    }

    &--icon-after {
      // Keep the chevron flush against the label.
      // `me-button` renders its icon as `<me-icon class="me-button__icon">`, which puts the
      // class on the host AND (via me-icon's `class` prop) on me-icon's inner `.me-icon`
      // div — so the generic `.me-button--icon-after .me-icon { margin-left: … }` in
      // elements/button/index.scss applies here too. It did not for a while: between
      // 643656f8 and 0272bddd me-button inlined `arrows--chevron-down` as a raw <svg> that
      // matched `.me-button__icon` but never `.me-icon`, so the margin (and the
      // `left: calc(-1 * var(--spacing-xs))` nudge that used to sit here to offset it) were
      // both dead and the glyph sat flush. That flush geometry is what shipped, and what
      // `sn-navbar-placeholder-type-2` was measured against — its SSR chevron is a raw <svg>
      // and cannot pick up a `.me-icon` rule, so any margin here reappears as a visible
      // sideways jump the moment the real navbar swaps in.
      .me-icon {
        margin-left: 0;
      }

      &::before {
        display: none;

        @include desktop-lg {
          display: initial;

          // Explicitly set left and right to prevent positioning shift when hovering over margin area
          // Higher specificity: .me-dropdown .me-button--icon-after::before (0,2,1)
          // vs .me-button--ghost.me-button--icon-after::before (0,2,1)
          // This wins due to cascade order
          left: 0;
          right: calc(var(--spacing-xl) + var(--spacing-2xs));
        }
      }

      .me-button__icon {
        @include size(var(--spacing-xl));

        aspect-ratio: 1;
        transition: transform 0.3s ease;
      }
    }

    &:hover {
      .me-button__icon {
        opacity: 1;
      }
    }

    // Higher specificity for hover: .me-dropdown .me-button.me-button--icon-after:hover::before (0,3,2)
    // vs .me-button--ghost.me-button--icon-after:hover::before (0,3,1)
    // This wins due to higher specificity
    &.me-button--icon-after:hover::before {
      @include desktop-lg {
        // Ensure left and right positions are fixed on hover to prevent shift
        left: 0;
        right: calc(var(--spacing-xl) + var(--spacing-2xs));
      }
    }

    &.is-active {
      // Direct child only. `me-button__icon` lands on TWO elements — Stencil always mirrors a
      // JSX `class` onto the custom-element host, and me-icon copies the same class onto its
      // inner container — so an unscoped selector rotated the host and the inner div 180° each.
      // The two compose to 360°: the chevron never appeared to flip, and the host's rotation
      // mirrored the inner box's offsets, so the glyph also jumped sideways while "open".
      // `>` matches the <me-icon> host in the real navbar and the raw <svg> in the SSR
      // placeholder, so exactly one box rotates in either DOM.
      > .me-button__icon {
        transform: rotate(180deg);
      }

      &--icon-after::before {
        @include desktop-lg {
          width: calc(100% - (var(--spacing-xl) + var(--spacing-2xs)));

          // Ensure left and right positions are fixed when active
          left: 0;
          right: calc(var(--spacing-xl) + var(--spacing-2xs));
        }
      }
    }
  }

  &__menu {
    position: relative;
    width: 100%;
    border: 0;
    margin: 0;
    padding: 0 var(--spacing-md);
    max-height: 0;
    overflow: hidden;
    transform: none;

    @include desktop-lg {
      position: absolute;
      top: var(--spacing-2xl);
      left: 0;
      z-index: 1000;
      padding: var(--spacing-xl);
      border: var(--border-size-sm) solid var(--color-neutral-black);
      background: var(--color-neutral-white);
      opacity: 0;
      visibility: hidden;
      transform: translateY(var(--spacing-xs));
      transition: all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
      overflow: hidden;
      width: auto;
      margin: initial;
      max-height: initial;

      .me-button {
        white-space: nowrap;
      }
    }

    &--open {
      max-height: 100%;
      opacity: 1;
      visibility: visible;
      padding-top: var(--spacing-md);
      padding-bottom: var(--spacing-md);

      @include desktop-lg {
        padding-top: var(--spacing-xl);
        padding-bottom: var(--spacing-xl);
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
        max-height: initial;
        transition: all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
        box-shadow: 0 4px 12px rgb(44 44 44 / 12%);
      }
    }
  }
}
